import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiLoader, FiSmartphone, FiArrowRight, FiX, FiCopy } from 'react-icons/fi';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const PromotionModal = ({ isOpen, onClose, promotion }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [progressText, setProgressText] = useState('Verificando disponibilidade...');
  const [progressWidth, setProgressWidth] = useState(0);
  const [generatedCoupon, setGeneratedCoupon] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setProgressWidth(0);
      setGeneratedCoupon('');
      setCopied(false);
      return;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    if (step === 2) {
      // Step 2: Animation Progress
      const animTime = (promotion?.animationTime || 3) * 1000;
      
      const intervalText1 = setTimeout(() => {
        setProgressText('Reservando benefício...');
        setProgressWidth(50);
      }, animTime / 3);

      const intervalText2 = setTimeout(() => {
        setProgressText('Gerando acesso...');
        setProgressWidth(85);
      }, (animTime / 3) * 2);

      const finishAnim = setTimeout(() => {
        setProgressWidth(100);
        setTimeout(() => setStep(3), 300); // Move to success step
      }, animTime);

      return () => {
        clearTimeout(intervalText1);
        clearTimeout(intervalText2);
        clearTimeout(finishAnim);
      };
    }

        if (step === 3 && !generatedCoupon) {
      // Step 3: Success (Gera Cupom e Exibe)
      const processCoupon = async () => {
        try {
          const randomString = Math.random().toString(36).substring(2, 8).toUpperCase();
          const couponCode = `PROMO10-${randomString}`;
          
          await addDoc(collection(db, 'coupons'), {
            code: couponCode,
            discountPercentage: 10,
            active: true,
            createdAt: serverTimestamp()
          });

          localStorage.setItem('activeCoupon', couponCode);
          setGeneratedCoupon(couponCode);
        } catch (error) {
          console.error('Erro ao gerar cupom:', error);
          setGeneratedCoupon('BEMVINDO10'); // Fallback
          localStorage.setItem('activeCoupon', 'BEMVINDO10');
        }
      };

      processCoupon();
    }
  }, [isOpen, step, promotion, onClose, generatedCoupon]);

  if (!isOpen || !promotion) return null;

  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
        />

        {/* Modal */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden"
          style={{ fontFamily: promotion.fontFamily || 'Inter, sans-serif' }}
        >
          {/* Header Image / Color */}
          <div 
            className="w-full relative flex items-end justify-center overflow-hidden"
            style={{ 
              backgroundColor: promotion.bannerColor || '#4f46e5',
              minHeight: promotion.backgroundImage ? '200px' : '120px'
            }}
          >
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 z-50 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full backdrop-blur-md transition-colors"
            >
              <FiX size={20} />
            </button>

            {promotion.backgroundImage && (
              <img 
                src={promotion.backgroundImage} 
                alt="Promoção"
                className="absolute inset-0 w-full h-full object-cover object-center opacity-90"
                style={{ imageRendering: 'high-quality' }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
            <div className="relative z-10 text-center px-6 pb-6 pt-12 w-full" style={{ color: promotion.textColor || '#ffffff' }}>
              {promotion.highlightText && (
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-[10px] uppercase tracking-widest font-bold mb-3 shadow-sm">
                  {promotion.highlightText}
                </span>
              )}
              <h3 className="text-2xl font-extrabold leading-tight shadow-black drop-shadow-lg">{promotion.title}</h3>
            </div>
          </div>

          <div className="p-8 text-center">
            {step === 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="w-16 h-16 mx-auto bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center mb-6 text-indigo-600 dark:text-indigo-400">
                  <FiSmartphone size={32} />
                </div>
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{promotion.subtitle || 'Você está quase lá!'}</h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-8 leading-relaxed">
                  {promotion.description}
                </p>
                <button 
                  onClick={() => {
                    setStep(2);
                    setProgressWidth(10);
                  }}
                  className="w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-black rounded-xl font-bold hover:shadow-lg hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
                >
                  {promotion.ctaText || 'Continuar'} <FiArrowRight />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6">
                <FiLoader className="w-12 h-12 text-indigo-600 mx-auto animate-spin mb-6" />
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">{progressText}</h4>
                <div className="w-full h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full rounded-full"
                    style={{ backgroundColor: promotion.bannerColor || '#4f46e5' }}
                    animate={{ width: `${progressWidth}%` }}
                    transition={{ ease: "easeInOut", duration: 0.5 }}
                  />
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="py-6">
                <div className="w-20 h-20 mx-auto bg-emerald-50 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4 text-emerald-500">
                  <FiCheckCircle size={40} />
                </div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Cupom Gerado!</h4>
                <p className="text-gray-500 text-sm mb-4">Você ganhou 10% de desconto na criação do seu app. Copie o código abaixo:</p>
                
                {generatedCoupon ? (
                  <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-xl flex items-center justify-between border border-gray-200 dark:border-gray-700 mb-6">
                    <span className="font-mono text-xl font-bold text-gray-900 dark:text-white tracking-wider">{generatedCoupon}</span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(generatedCoupon);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="p-2 bg-white dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300 hover:text-indigo-600 transition-colors shadow-sm"
                    >
                      {copied ? <FiCheckCircle className="text-emerald-500" /> : <FiCopy />}
                    </button>
                  </div>
                ) : (
                  <div className="h-16 flex items-center justify-center mb-6">
                    <FiLoader className="animate-spin text-indigo-500" size={24} />
                  </div>
                )}

                <button 
                  onClick={() => navigate('/app-creation', { state: { allowNew: true } })}
                  className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-md"
                >
                  Continuar para Criação
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PromotionModal;
